import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus } from 'lucide-react';
import Modal from './ui/Modal';
import { useConfirm } from './ui/ConfirmDialog';
import { useToast } from './ToastProvider';
import apiService from '../services/apiService';
import { useI18n } from '../utils/i18n';

// #80: Persönliche API-Keys — Self-Service aus dem Optionen-Menü.
// Die Keys agieren mit den Rechten des angemeldeten Benutzers; die
// Verwaltung ist serverseitig auf Browser-Sessions beschränkt (ein Key
// kann keine weiteren Keys erzeugen/widerrufen). Aufbau analog zum
// Admin-Tab „API-Keys", nur ohne „Agiert als Benutzer".
const PersonalApiKeysModal = ({ open, onClose }) => {
  const { getLocale, t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newKey, setNewKey] = useState({ name: '', lifetimeDays: '90' });
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.getMyApiKeys();
      setKeys(resp.apiKeys || []);
    } catch (err) {
      setError(err.response?.data?.error || t('personalKeys.loadError', 'Fehler beim Laden der API-Keys.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      setCreatedKey(null);
      loadKeys();
    }
  }, [open, loadKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKey.name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const resp = await apiService.createMyApiKey({
        name: newKey.name.trim(),
        lifetimeDays: newKey.lifetimeDays
      });
      setCreatedKey({ name: resp.apiKey?.name || newKey.name.trim(), key: resp.key });
      setNewKey({ name: '', lifetimeDays: '90' });
      await loadKeys();
    } catch (err) {
      setError(err.response?.data?.error || t('personalKeys.createError', 'Fehler beim Erzeugen des API-Keys.'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key) => {
    if (!(await confirm({
      message: t('admin.apiKeys.revokeConfirm', 'Diesen API-Key wirklich widerrufen? Anwendungen, die ihn benutzen, verlieren sofort den Zugriff.'),
      danger: true
    }))) {
      return;
    }
    try {
      await apiService.revokeMyApiKey(key.id);
      pushToast({ message: t('admin.apiKeys.revoked', 'API-Key widerrufen.'), type: 'success' });
      await loadKeys();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.apiKeys.revokeError', 'Fehler beim Widerrufen des API-Keys.'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('personalKeys.title', 'Meine API-Keys')}
      icon={<Key size={18} />}
      maxWidth="720px"
    >
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
        {t('personalKeys.intro', 'Bearer-Keys für eigene Skripte und Agenten. Ein Key hat genau deine Benutzerrechte und wird nur einmal angezeigt.')}
      </p>
      {/* #202: Reichweite + Rotationsregel sichtbar im Dialog, nicht nur in der Doku */}
      <div className="alert alert-warning" style={{ fontSize: 13 }}>
        {t('admin.apiKeys.reachNotice', 'Ein Key liest alles, was der zugeordnete Benutzer lesen darf — auch ticket-verknüpfte E-Mail- und Teams-Inhalte. Laufzeit ist Pflicht: Abgelaufene Keys werden nicht verlängert, sondern neu ausgestellt (Rotation).')}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {createdKey && (
        <div className="alert alert-success" style={{ wordBreak: 'break-all' }}>
          <strong>{t('admin.apiKeys.createdFor', 'Neuer Key für')} „{createdKey.name}":</strong>
          <div style={{ fontFamily: 'monospace', margin: '6px 0' }}>{createdKey.key}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { try { navigator.clipboard.writeText(createdKey.key); } catch (_) {} }}>
              {t('admin.apiKeys.copy', 'Kopieren')}
            </button>
            <span style={{ fontSize: 12 }}>{t('admin.apiKeys.showOnce', 'Der Key wird nur dieses eine Mal angezeigt — jetzt sicher ablegen!')}</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreatedKey(null)}>×</button>
          </div>
        </div>
      )}

      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="form-group" style={{ minWidth: 220, flex: 1 }}>
          <label>{t('admin.apiKeys.name', 'Name')} *</label>
          <input
            type="text"
            className="form-input"
            value={newKey.name}
            onChange={(e) => setNewKey(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t('personalKeys.namePlaceholder', 'z. B. Kalender-Skript')}
            disabled={creating}
            required
          />
        </div>
        <div className="form-group">
          {/* #202: Laufzeit ist Pflicht — „Unbefristet" gibt es nicht mehr */}
          <label>{t('admin.apiKeys.lifetime', 'Laufzeit')} *</label>
          <select
            className="form-input"
            value={newKey.lifetimeDays}
            onChange={(e) => setNewKey(prev => ({ ...prev, lifetimeDays: e.target.value }))}
            disabled={creating}
          >
            {['1', '7', '30', '90', '180', '365'].map(d => (
              <option key={d} value={d}>{d} {t('admin.apiKeys.days', 'Tage')}</option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn btn-primary" disabled={creating || !newKey.name.trim()}>
          <Plus size={16} />
          {creating ? t('admin.apiKeys.creating', 'Erzeuge…') : t('admin.apiKeys.create', 'Key erzeugen')}
        </button>
      </form>

      {keys.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="users-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{t('admin.apiKeys.name', 'Name')}</th>
                <th>{t('admin.apiKeys.prefix', 'Key')}</th>
                <th>{t('admin.apiKeys.expires', 'Läuft ab')}</th>
                <th>{t('admin.apiKeys.lastUsed', 'Zuletzt benutzt')}</th>
                <th>{t('admin.apiKeys.status', 'Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} style={k.status !== 'active' ? { opacity: 0.55 } : undefined}>
                  <td>{k.name}</td>
                  <td style={{ fontFamily: 'monospace' }}>{k.keyPrefix}…</td>
                  <td>{k.expiresAt ? new Date(k.expiresAt).toLocaleDateString(getLocale()) : '—'}</td>
                  <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString(getLocale()) : t('common.never', 'Nie')}</td>
                  <td>
                    {k.status === 'active' && <span style={{ color: 'var(--color-success)' }}>{t('admin.apiKeys.statusActive', 'aktiv')}</span>}
                    {k.status === 'expired' && <span style={{ color: 'var(--color-orange)' }}>{t('admin.apiKeys.statusExpired', 'abgelaufen')}</span>}
                    {k.status === 'revoked' && (
                      <span style={{ color: 'var(--color-danger)' }} title={k.revokedBy ? `${t('admin.apiKeys.revokedBy', 'Widerrufen von')} ${k.revokedBy}` : undefined}>
                        {t('admin.apiKeys.statusRevoked', 'widerrufen')}
                      </span>
                    )}
                  </td>
                  <td>
                    {k.status !== 'revoked' && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleRevoke(k)}>
                        {t('admin.apiKeys.revoke', 'Widerrufen')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {keys.length === 0 && !loading && (
        <div className="empty-state" style={{ textAlign: 'center', padding: '24px 0' }}>
          <Key size={40} style={{ color: 'var(--color-text-muted)', marginBottom: 12 }} />
          <p style={{ margin: 0 }}>{t('personalKeys.none', 'Noch keine eigenen API-Keys angelegt.')}</p>
        </div>
      )}
    </Modal>
  );
};

export default PersonalApiKeysModal;
